import { GetServerSideProps } from 'next';
import { ReactElement, useEffect, useState } from 'react';
import { ImagesAuthenticationSignInSignInBannerWebp } from '@/assets/images_authentication_sign-in.assets';
import {
  ImagesAuthenticationCommonAppleLoginSvg,
  ImagesAuthenticationCommonGoogleLoginSvg,
} from '@/assets/images_authentication_common.assets';
import Image from 'next/image';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import DefaultButton from '@/components/common/default/DefaultButton';
import { PrimaryButton, PrimaryButtonLink } from '@/components/common/PrimaryButton';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { AuthenticationEnum } from '@/types/translations/AuthenticationEnum';
import DefaultLink from '@/components/common/default/DefaultLink';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import getSignUpServerSideProps from '@/lib/serverSideProps/serverSidePropsPages/getSignUpServerSideProps';
import AuthInput from '@/components/auth/AuthInput';
import validator from 'validator';
import { isPasswordValid } from '@/utilites/auth.utils';
import AuthSelect from '@/components/auth/AuthSelect';
import changeLocalClient from '@/utilites/change-local-client';
import { SignUpSpinner } from '@/components/common/LoadingSpinners';
import doSignUp from '@/api/doSignUp';
import { SignUpServerProps } from '@/types/props/pages';
import { AuthResult } from '@/types/api/auth';
import { BaseApiResponse } from '@/types/api/api-v2';
import { AxiosError } from 'axios';
import AuthCommonLayout from '@/components/auth/AuthCommonLayout';
import SignUpMeta from '@/components/seo/meta/SignUpMeta';
import { setCookie } from 'cookies-next';
import AuthCheckbox from '@/components/auth/AuthCheckbox';
import doSubscribeNewsletter from '@/api/doSubscribeNewsletter';
import { useGoogleCaptcha } from '@/context/GoogleCaptchaProvider';
import ReCaptchaDisclaimer from '@/components/common/RecaptchaDisclaimer';
import cookiesConstants from '@/constants/cookies.constants';
export default function SignUp({ referralCode, referralFlow }: SignUpServerProps) {
  const { executeRecaptcha, isScriptLoaded } = useGoogleCaptcha();

  const router = useRouter();
  const { query, locale = 'EN', replace } = router;
  const { error } = query;

  const { translate } = useTypedTranslation<AuthenticationEnum>('authentication');

  const [authResult, setAuthResult] = useState<AuthResult | null>(null);
  const [signUpCompleted, setSignUpCompleted] = useState(false);
  const [referralCodeValue, setReferralCodeValue] = useState<string | null>(referralCode);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState<boolean>(false);
  const [loadingSignUp, setLoadingSignUp] = useState<boolean>(false);
  const [loadingDueLanguageChange, setLoadingDueLanguageChange] = useState<boolean>(false);
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState<boolean>(false);
  const [ableToSignUp, setAbleToSignUp] = useState<boolean>(true);

  const [signUpStep, setSignUpStep] = useState<0 | 1>(0);

  const [signUpData, setSignUpData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    verifyPassword: string;
    locale: string;
  }>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    verifyPassword: '',
    locale: locale.toUpperCase(),
  });
  const [signUpDataErrors, setSignUpDataErrors] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    verifyPassword: string;
  }>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    verifyPassword: '',
  });

  const [debounceTimers, setDebounceTimers] = useState<{
    [key: string]: NodeJS.Timeout | null;
  }>({
    firstName: null,
    lastName: null,
    email: null,
    password: null,
  });

  const handleChange = (
    field: keyof typeof signUpData,
    value: string,
    validateFn: (value: string) => string
  ) => {
    setSignUpData(prev => ({ ...prev, [field]: value }));

    if (!value) {
      setSignUpDataErrors(prev => ({ ...prev, [field]: '' }));
      return;
    }

    // Clear previous debounce timer
    if (debounceTimers[`${field}`]) {
      clearTimeout(debounceTimers[`${field}`]!);
    }

    // Set a new debounce timer
    const timer = setTimeout(() => {
      const error = validateFn(value);
      setSignUpDataErrors(prev => ({ ...prev, [field]: error }));
    }, 500); // 500ms debounce time

    setDebounceTimers(prev => ({ ...prev, [field]: timer }));
  };

  const validateFirstName = (value: string) =>
    validator.isAlpha(value, undefined, { ignore: ' ' })
      ? ''
      : translate(AuthenticationEnum.name_must_contain_only_letters);

  const validateLastName = (value: string) =>
    validator.isAlpha(value, undefined, { ignore: ' ' })
      ? ''
      : translate(AuthenticationEnum.last_name_must_contain_only_letters);

  const validateEmail = (value: string) =>
    validator.isEmail(value) ? '' : translate(AuthenticationEnum.invalid_email);

  const validatePassword = (value: string) => {
    const passwordValid = isPasswordValid(value);
    return passwordValid.isValid ? '' : translate(passwordValid.error);
  };

  const validateVerifyPassword = (value: string) =>
    signUpData.password === value ? '' : translate(AuthenticationEnum.passwords_do_not_match);

  async function handleSubmitSignUp(e: React.FormEvent<HTMLFormElement>) {
    setLoadingSignUp(true);
    e.preventDefault();
    if (isScriptLoaded) {
      if (acceptPrivacyPolicy === true) {
        await doSubscribeNewsletter(signUpData.email).catch(err => console.error(err));
      }
      if (signUpData.password !== signUpData.verifyPassword) {
        setLoadingSignUp(false);
        return toast.error(translate(AuthenticationEnum.passwords_do_not_match));
      }
      try {
        const captcha = await executeRecaptcha('sign_up');
        const tokenData = await doSignUp(
          signUpData.email,
          signUpData.password,
          signUpData.firstName,
          signUpData.lastName,
          signUpData.locale.toUpperCase(),
          captcha
        );
        toast.success(translate(AuthenticationEnum.sign_up_successful));
        setAuthResult(tokenData);
        setCookie(cookiesConstants.COOKIES.ONBOARDING.name, 'true', {
          maxAge: 60 * 60 * 24 * 365,
        });
        if (referralFlow) {
          setLoadingSignUp(false);
          setSignUpCompleted(true);
        } else {
          // Create a hidden form to securely submit token data
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = '/api/auth/sign-up/token';
          form.style.display = 'none';

          // Add token data as hidden inputs
          const createHiddenInput = (name: string, value: string) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
          };

          createHiddenInput('accessToken', tokenData.access_token);
          createHiddenInput('refreshToken', tokenData.refresh_token ?? '');
          createHiddenInput('accessTokenExpiresAt', tokenData.accessTokenExpiresAt);
          createHiddenInput('sessionToken', tokenData.sessionToken.token);
          createHiddenInput('sessionTokenOptions', JSON.stringify(tokenData.sessionToken.options));

          // Add the form to the document and submit it
          document.body.appendChild(form);
          form.submit();
        }
      } catch (err: unknown) {
        setLoadingSignUp(false);
        if (err instanceof AxiosError) {
          const axiosErrorData = err.response?.data as BaseApiResponse<null> | null;
          if (axiosErrorData) {
            const axiosErrorMessage = axiosErrorData?.meta?.message;
            if (axiosErrorMessage === 'Captcha is required') {
              return toast.error(translate(AuthenticationEnum.captcha_is_required));
            } else if (axiosErrorMessage === 'Invalid captcha') {
              return toast.error(translate(AuthenticationEnum.invalid_captcha));
            } else if (axiosErrorMessage === 'User already exists') {
              return toast.error(
                translate(AuthenticationEnum.a_user_with_this_email_already_exists)
              );
            } else if (axiosErrorMessage === 'First name must be alphabetic') {
              return toast.error(translate(AuthenticationEnum.first_name_must_be_alphabetic));
            } else if (axiosErrorMessage === 'Last name must be alphabetic') {
              return toast.error(translate(AuthenticationEnum.last_name_must_be_alphabetic));
            } else if (axiosErrorMessage === 'Invalid email') {
              return toast.error(translate(AuthenticationEnum.invalid_email));
            } else if (axiosErrorMessage === "Password doesn't meet the requirements") {
              return toast.error(
                translate(AuthenticationEnum.password_doesnt_meet_the_requirements)
              );
            }
          }
        }
        return toast.error(translate(AuthenticationEnum.an_unexpected_error_occurred));
      }
    } else {
      setLoadingSignUp(false);
      return toast.error(translate(AuthenticationEnum.captcha_is_required));
    }
  }

  async function handleSubmitWithCaptcha(e: React.FormEvent<HTMLFormElement>, action: string) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    if (isScriptLoaded) {
      const captcha = await executeRecaptcha(action);

      formData.append('captcha', captcha);
      const tempForm = document.createElement('form');
      tempForm.action = form.action;
      tempForm.method = form.method;

      for (const [name, value] of formData.entries()) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value as string;
        tempForm.appendChild(input);
      }

      document.body.appendChild(tempForm);
      tempForm.submit();
    } else {
      toast.error(translate(AuthenticationEnum.captcha_is_required));
    }
  }

  useEffect(() => {
    if (error) {
      replace('/auth/sign-up', undefined, { shallow: true });
      if (error === 'Invalid email or password') {
        toast.error(translate(AuthenticationEnum.invalid_email_or_password));
      } else if (error === 'An error occurred') {
        toast.error(translate(AuthenticationEnum.an_unexpected_error_occurred));
      } else if (error === 'Captcha is required') {
        toast.error(translate(AuthenticationEnum.captcha_is_required));
      } else if (error === 'Invalid captcha') {
        toast.error(translate(AuthenticationEnum.invalid_captcha));
      } else {
        toast.error(translate(AuthenticationEnum.an_unexpected_error_occurred));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (signUpStep === 0) {
      setAbleToSignUp(
        Object.values(signUpDataErrors).every(error => !error) &&
          Boolean(signUpData.firstName) &&
          Boolean(signUpData.lastName) &&
          Boolean(signUpData.locale)
      );
    } else if (signUpStep === 1) {
      setAbleToSignUp(
        Object.values(signUpDataErrors).every(error => !error) &&
          Boolean(signUpData.email) &&
          Boolean(signUpData.password) &&
          Boolean(signUpData.verifyPassword) &&
          signUpData.password === signUpData.verifyPassword
      );
    }
  }, [signUpData, signUpStep, signUpDataErrors]);

  if (loadingDueLanguageChange || loadingSignUp) {
    return (
      <>
        <SignUpMeta />
        <AuthCommonLayout bgImage={ImagesAuthenticationSignInSignInBannerWebp} coloredBg={false}>
          <div>
            <SignUpSpinner localeChange={loadingDueLanguageChange} />
          </div>
        </AuthCommonLayout>
      </>
    );
  }

  if (referralFlow && signUpCompleted) {
    return (
      <>
        <SignUpMeta />
        <AuthCommonLayout bgImage={ImagesAuthenticationSignInSignInBannerWebp} coloredBg={false}>
          <h1 className="tw-font-prata tw-text-5xl tw-font-normal tw-leading-[65.04px] tw-text-[#232323] max-nine:tw-text-center max-md:tw-text-3xl">
            {translate(AuthenticationEnum.got_a_referral_code_interrogation_sign)}
          </h1>
          <p className="tw-font-plusJakartaSans tw-text-base tw-font-medium tw-leading-[20.16px] tw-text-[#54606FCC] max-nine:tw-text-center">
            {translate(
              AuthenticationEnum.enter_the_code_below_and_enjoy_exclusive_benefits_exclamation_sign
            )}
          </p>
          <form
            action={`/api/referrals/${referralCodeValue}/redeem`}
            method="POST"
            onSubmit={e => handleSubmitWithCaptcha(e, 'redeem_referral_code')}
          >
            <input type="hidden" name="accessToken" value={authResult?.access_token} />
            <input type="hidden" name="refreshToken" value={authResult?.refresh_token} />
            <input
              type="hidden"
              name="accessTokenExpiresAt"
              value={authResult?.accessTokenExpiresAt}
            />
            <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-start">
              <AuthInput
                name="referralCode"
                type="text"
                label={translate(AuthenticationEnum.referral_code)}
                placeholder={translate(AuthenticationEnum.insert_your_referral_code)}
                value={referralCodeValue || ''}
                onChange={e => setReferralCodeValue(e.target.value)}
                invalidMessage=""
              />
            </div>
            <div className="tw-mt-1 tw-w-full">
              <PrimaryButton
                type="submit"
                className="tw-w-full tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-text-white nine:tw-max-w-full"
              >
                {translate(AuthenticationEnum.submit)}
              </PrimaryButton>
            </div>
          </form>
        </AuthCommonLayout>
      </>
    );
  }

  if (signUpCompleted) {
    return (
      <>
        <SignUpMeta />
        <AuthCommonLayout bgImage={ImagesAuthenticationSignInSignInBannerWebp} coloredBg={false}>
          <h1 className="tw-font-prata tw-text-5xl tw-font-normal tw-leading-[65.04px] tw-text-[#232323] max-nine:tw-text-center max-md:tw-text-3xl">
            {translate(AuthenticationEnum.check_your_email_for_verification)} 🎉
          </h1>
          <p className="tw-font-plusJakartaSans tw-text-base tw-font-medium tw-leading-[20.16px] tw-text-[#54606FCC] max-nine:tw-text-center">
            {translate(AuthenticationEnum.check_your_email_for_verification_sub)}
          </p>
          <div className="tw-mt-5 tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-4 child:tw-min-w-40 child:tw-flex-grow child:tw-basis-[calc(50%-1rem)]">
            {authResult && (
              <PrimaryButtonLink
                outlined={true}
                href={`/api/auth/sign-up/token?accessToken=${authResult.access_token}&refreshToken=${authResult.refresh_token}&accessTokenExpiresAt=${authResult.accessTokenExpiresAt}`}
              >
                {translate(AuthenticationEnum.sign_in)} 🚀
              </PrimaryButtonLink>
            )}
            <PrimaryButtonLink href={`/`}>
              {translate(AuthenticationEnum.go_back_to_the_home_page)}
            </PrimaryButtonLink>
          </div>
        </AuthCommonLayout>
      </>
    );
  }

  if (signUpStep === 1) {
    return (
      <>
        <SignUpMeta />
        <AuthCommonLayout bgImage={ImagesAuthenticationSignInSignInBannerWebp} coloredBg={false}>
          <h1 className="tw-font-prata tw-text-5xl tw-font-normal tw-leading-[65.04px] tw-text-[#232323] max-nine:tw-text-center max-md:tw-text-3xl">
            {translate(AuthenticationEnum.sign_up)}
          </h1>
          <p className="tw-font-plusJakartaSans tw-text-base tw-font-medium tw-leading-[20.16px] tw-text-[#54606FCC] max-nine:tw-text-center">
            {translate(AuthenticationEnum.sign_up_to_enjoy_the_feature_of_spiritory)}
          </p>
          <form className="tw-contents" onSubmit={handleSubmitSignUp}>
            <div className="tw-w-full tw-space-y-4">
              <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-start">
                <AuthInput
                  name="email"
                  type="email"
                  label={translate(AuthenticationEnum.email)}
                  placeholder={translate(AuthenticationEnum.email)}
                  value={signUpData.email}
                  onChange={e => handleChange('email', e.target.value, validateEmail)}
                  invalidMessage={signUpDataErrors.email}
                />
              </div>
              <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-start">
                <div className="tw-relative tw-w-full">
                  <AuthInput
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    label={translate(AuthenticationEnum.password)}
                    placeholder={translate(AuthenticationEnum.password)}
                    value={signUpData.password}
                    invalidMessage={signUpDataErrors.password}
                    onChange={e => handleChange('password', e.target.value, validatePassword)}
                  />
                  <DefaultButton
                    aria-label={
                      showPassword
                        ? translate(AuthenticationEnum.hide_password)
                        : translate(AuthenticationEnum.show_password)
                    }
                    className="tw-absolute tw-bottom-7 tw-right-4 -tw-translate-y-2/4 tw-cursor-pointer"
                    onClick={() => setShowPassword(showPassword ? false : true)}
                  >
                    {showPassword ? (
                      <IoMdEyeOff className="tw-text-xl tw-text-[#9A9A9A]" />
                    ) : (
                      <IoMdEye className="tw-text-xl tw-text-[#9A9A9A]" />
                    )}
                  </DefaultButton>
                </div>
              </div>
              <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-start">
                <div className="tw-relative tw-w-full">
                  <AuthInput
                    name="verifyPassword"
                    type={showVerifyPassword ? 'text' : 'password'}
                    label={translate(AuthenticationEnum.verify_password)}
                    placeholder={translate(AuthenticationEnum.verify_password)}
                    value={signUpData.verifyPassword}
                    invalidMessage={signUpDataErrors.verifyPassword}
                    onChange={e =>
                      handleChange('verifyPassword', e.target.value, validateVerifyPassword)
                    }
                  />
                  <DefaultButton
                    aria-label={
                      showPassword
                        ? translate(AuthenticationEnum.hide_password)
                        : translate(AuthenticationEnum.show_password)
                    }
                    className="tw-absolute tw-bottom-7 tw-right-4 -tw-translate-y-2/4 tw-cursor-pointer"
                    onClick={() => {
                      setShowVerifyPassword(showVerifyPassword ? false : true);
                    }}
                  >
                    {showVerifyPassword ? (
                      <IoMdEyeOff className="tw-text-xl tw-text-[#9A9A9A]" />
                    ) : (
                      <IoMdEye className="tw-text-xl tw-text-[#9A9A9A]" />
                    )}
                  </DefaultButton>
                </div>
              </div>
            </div>
            <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-start">
              <div className="tw-relative tw-w-full">
                <AuthCheckbox
                  checked={acceptPrivacyPolicy}
                  onChange={setAcceptPrivacyPolicy}
                  name="acceptPrivacyPolicy"
                  label={translate(AuthenticationEnum.accept_privacy_policy).replace(
                    '{{privacy_policy}}',
                    `<a href="/data-privacy" target="_blank" rel="noopener noreferrer">${
                      locale.toLowerCase() === 'en'
                        ? 'privacy policy'
                        : locale.toLowerCase() === 'de'
                          ? 'Datenschutzrichtlinie'
                          : locale.toLowerCase() === 'es'
                            ? 'política de privacidad'
                            : locale.toLowerCase() === 'fr'
                              ? 'politique de confidentialité'
                              : locale.toLowerCase() === 'it'
                                ? 'informativa sulla privacy'
                                : 'privacy policy'
                    }</a>`
                  )}
                />
              </div>
            </div>
            <div className="tw-mt-5 tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-4 child:tw-min-w-40 child:tw-flex-grow child:tw-basis-[calc(50%-1rem)]">
              <PrimaryButton
                outlined={true}
                onClick={e => {
                  e.stopPropagation();
                  setSignUpStep(0);
                }}
                type="button"
                disabled={false}
              >
                {translate(AuthenticationEnum.back)}
              </PrimaryButton>
              <PrimaryButton
                type="submit"
                className="tw-w-full tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-text-white nine:tw-max-w-full"
                disabled={
                  !isScriptLoaded ||
                  loadingSignUp ||
                  loadingDueLanguageChange ||
                  ableToSignUp === false
                }
              >
                {translate(AuthenticationEnum.sign_up)}
              </PrimaryButton>
            </div>
            <div className="tw-pt-2">
              <small
                className="tw-text-center tw-text-xs-lower tw-text-gray-400 tw-transition-colors tw-duration-300 hover:tw-text-gray-600"
                dangerouslySetInnerHTML={{
                  __html: translate(AuthenticationEnum.privacy_message).replace(
                    '{{privacyPolicy}}',
                    `<a href="/data-privacy" target="_blank" rel="noopener noreferrer">${
                      locale.toLowerCase() === 'en'
                        ? 'privacy policy'
                        : locale.toLowerCase() === 'de'
                          ? 'Datenschutzrichtlinie'
                          : locale.toLowerCase() === 'es'
                            ? 'política de privacidad'
                            : locale.toLowerCase() === 'fr'
                              ? 'politique de confidentialité'
                              : locale.toLowerCase() === 'it'
                                ? 'informativa sulla privacy'
                                : 'privacy policy'
                    }</a>`
                  ),
                }}
              ></small>
            </div>
          </form>
          <div className="tw-my-4 tw-flex tw-items-center tw-justify-center tw-gap-3">
            <hr className="tw-h-px tw-w-full tw-bg-[#DEDFE2] tw-bg-opacity-100" />
            <span className="tw-font-plusJakartaSans tw-text-base tw-font-medium tw-leading-[20.16px] tw-text-[#6E6E6E]">
              {translate(AuthenticationEnum.or)}
            </span>
            <hr className="tw-h-px tw-w-full tw-bg-[#DEDFE2] tw-bg-opacity-100" />
          </div>
          <div className="tw-w-full">
            <form
              className="tw-w-full"
              action={`/api/auth/sign-in/social/google-oauth2`}
              method="POST"
              onSubmit={e => handleSubmitWithCaptcha(e, 'sign_up_google')}
            >
              <DefaultButton
                className="tw-flex tw-w-full tw-basis-full tw-items-center tw-justify-center tw-gap-3 tw-text-nowrap tw-rounded-xl tw-border tw-border-solid tw-border-platinum-gray tw-px-2 tw-py-3 tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-text-[#232323] tw-transition-all tw-duration-200 hover:tw-border-[#C4C5C8] hover:tw-bg-[#F4F4F6] focus:tw-border-[#4285F4] focus:tw-ring-2 focus:tw-ring-[#4285F4] active:tw-border-[#A0A1A4] active:tw-bg-[#E0E0E2] disabled:tw-cursor-not-allowed disabled:tw-opacity-50 max-md:tw-w-full max-sm:tw-text-xs nine:tw-max-w-full"
                type="submit"
                disabled={!isScriptLoaded}
              >
                {translate(AuthenticationEnum.continue_with_google)}
                <Image
                  src={ImagesAuthenticationCommonGoogleLoginSvg}
                  alt="Google login"
                  width={24}
                  height={24}
                />
              </DefaultButton>
            </form>
          </div>
          <div className="tw-mt-4 tw-w-full">
            <form
              className="tw-w-full"
              action={`/api/auth/sign-in/social/apple`}
              method="POST"
              onSubmit={e => handleSubmitWithCaptcha(e, 'sign_up_apple')}
            >
              <DefaultButton
                className="tw-flex tw-w-full tw-basis-full tw-items-center tw-justify-center tw-gap-3 tw-text-nowrap tw-rounded-xl tw-border tw-border-solid tw-border-platinum-gray tw-px-2 tw-py-3 tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-text-[#232323] tw-transition-all tw-duration-200 hover:tw-border-[#C4C5C8] hover:tw-bg-[#F4F4F6] focus:tw-border-[#4285F4] focus:tw-ring-2 focus:tw-ring-[#4285F4] active:tw-border-[#A0A1A4] active:tw-bg-[#E0E0E2] disabled:tw-cursor-not-allowed disabled:tw-opacity-50 max-md:tw-w-full max-sm:tw-text-xs nine:tw-max-w-full"
                type="submit"
                disabled={!isScriptLoaded}
              >
                {translate(AuthenticationEnum.continue_with_apple)}
                <Image
                  src={ImagesAuthenticationCommonAppleLoginSvg}
                  alt="Apple login"
                  width={24}
                  height={24}
                />
              </DefaultButton>
            </form>
          </div>
          <div className="tw-mt-7 tw-flex tw-items-center tw-justify-center">
            <p className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-font-plusJakartaSans tw-text-lg tw-font-medium tw-leading-[22.68px] tw-text-[#6C6C6C] max-nine:tw-text-sm">
              {translate(AuthenticationEnum.already_have_an_account)}{' '}
              <DefaultLink
                href="/auth/sign-in"
                className="tw-text-main tw-underline tw-underline-offset-4"
              >
                {translate(AuthenticationEnum.sign_in)}
              </DefaultLink>
            </p>
          </div>
        </AuthCommonLayout>
      </>
    );
  }

  return (
    <>
      <SignUpMeta />

      <AuthCommonLayout bgImage={ImagesAuthenticationSignInSignInBannerWebp} coloredBg={false}>
        <h1 className="tw-font-prata tw-text-5xl tw-font-normal tw-leading-[65.04px] tw-text-[#232323] max-nine:tw-text-center max-md:tw-text-3xl">
          {translate(AuthenticationEnum.sign_up)}
        </h1>
        <p className="tw-font-plusJakartaSans tw-text-base tw-font-medium tw-leading-[20.16px] tw-text-[#54606FCC] max-nine:tw-text-center">
          {translate(AuthenticationEnum.sign_up_to_enjoy_the_feature_of_spiritory)}
        </p>
        <div className="tw-contents">
          <div className="tw-w-full tw-space-y-4">
            <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-start">
              <AuthInput
                name="firstName"
                type="firstName"
                label={translate(AuthenticationEnum.first_name)}
                placeholder={translate(AuthenticationEnum.first_name)}
                value={signUpData.firstName}
                onChange={e => handleChange('firstName', e.target.value, validateFirstName)}
                invalidMessage={signUpDataErrors.firstName}
              />
            </div>
            <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-start">
              <AuthInput
                name="lastName"
                type="lastName"
                label={translate(AuthenticationEnum.last_name)}
                placeholder={translate(AuthenticationEnum.last_name)}
                value={signUpData.lastName}
                onChange={e => handleChange('lastName', e.target.value, validateLastName)}
                invalidMessage={signUpDataErrors.lastName}
              />
            </div>
            <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-start tw-pb-2">
              <AuthSelect
                name="language"
                label={translate(AuthenticationEnum.language)}
                defaultOptionText={translate(AuthenticationEnum.select_a_language)}
                value={signUpData.locale}
                onChange={async e => {
                  setLoadingDueLanguageChange(true);
                  setSignUpData(prev => ({
                    ...prev,
                    locale: e.target.value,
                  }));
                  await changeLocalClient(e.target.value, router);
                  setLoadingDueLanguageChange(false);
                }}
                options={[
                  {
                    label: `${translate(AuthenticationEnum.english)} (English)`,
                    value: 'EN',
                  },
                  {
                    label: `${translate(AuthenticationEnum.spanish)} (Español)`,
                    value: 'ES',
                  },
                  {
                    label: `${translate(AuthenticationEnum.french)} (Français)`,
                    value: 'FR',
                  },
                  {
                    label: `${translate(AuthenticationEnum.italian)} (Italiano)`,
                    value: 'IT',
                  },
                  {
                    label: `${translate(AuthenticationEnum.german)} (Deutsch)`,
                    value: 'DE',
                  },
                ]}
              />
            </div>
          </div>
          <div className="tw-mt-5 tw-w-full">
            <PrimaryButton
              type="submit"
              className="tw-w-full tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-text-white nine:tw-max-w-full"
              disabled={
                loadingSignUp ||
                loadingDueLanguageChange ||
                !signUpData.firstName ||
                !signUpData.lastName ||
                !signUpData.locale ||
                ableToSignUp === false
              }
              onClick={e => {
                e.stopPropagation();
                setSignUpStep(1);
              }}
            >
              {translate(AuthenticationEnum.next)}
            </PrimaryButton>
            <ReCaptchaDisclaimer />
          </div>
        </div>
        <div className="tw-my-4 tw-flex tw-items-center tw-justify-center tw-gap-3">
          <hr className="tw-h-px tw-w-full tw-bg-[#DEDFE2] tw-bg-opacity-100" />
          <span className="tw-font-plusJakartaSans tw-text-base tw-font-medium tw-leading-[20.16px] tw-text-[#6E6E6E]">
            {translate(AuthenticationEnum.or)}
          </span>
          <hr className="tw-h-px tw-w-full tw-bg-[#DEDFE2] tw-bg-opacity-100" />
        </div>
        <div className="tw-w-full">
          <form
            className="tw-w-full"
            action={`/api/auth/sign-in/social/google-oauth2`}
            method="POST"
            onSubmit={e => handleSubmitWithCaptcha(e, 'sign_up_google')}
          >
            <DefaultButton
              className="tw-flex tw-w-full tw-basis-full tw-items-center tw-justify-center tw-gap-3 tw-text-nowrap tw-rounded-xl tw-border tw-border-solid tw-border-platinum-gray tw-px-2 tw-py-3 tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-text-[#232323] tw-transition-all tw-duration-200 hover:tw-border-[#C4C5C8] hover:tw-bg-[#F4F4F6] focus:tw-border-[#4285F4] focus:tw-ring-2 focus:tw-ring-[#4285F4] active:tw-border-[#A0A1A4] active:tw-bg-[#E0E0E2] disabled:tw-cursor-not-allowed disabled:tw-opacity-50 max-md:tw-w-full max-sm:tw-text-xs nine:tw-max-w-full"
              type="submit"
              disabled={!isScriptLoaded}
            >
              {translate(AuthenticationEnum.continue_with_google)}
              <Image
                src={ImagesAuthenticationCommonGoogleLoginSvg}
                alt="Google login"
                width={24}
                height={24}
              />
            </DefaultButton>
          </form>
        </div>
        <div className="tw-mt-4 tw-w-full">
          <form
            className="tw-w-full"
            action={`/api/auth/sign-in/social/apple`}
            method="POST"
            onSubmit={e => handleSubmitWithCaptcha(e, 'sign_up_apple')}
          >
            <DefaultButton
              className="tw-flex tw-w-full tw-basis-full tw-items-center tw-justify-center tw-gap-3 tw-text-nowrap tw-rounded-xl tw-border tw-border-solid tw-border-platinum-gray tw-px-2 tw-py-3 tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-text-[#232323] tw-transition-all tw-duration-200 hover:tw-border-[#C4C5C8] hover:tw-bg-[#F4F4F6] focus:tw-border-[#4285F4] focus:tw-ring-2 focus:tw-ring-[#4285F4] active:tw-border-[#A0A1A4] active:tw-bg-[#E0E0E2] disabled:tw-cursor-not-allowed disabled:tw-opacity-50 max-md:tw-w-full max-sm:tw-text-xs nine:tw-max-w-full"
              type="submit"
              disabled={!isScriptLoaded}
            >
              {translate(AuthenticationEnum.continue_with_apple)}
              <Image
                src={ImagesAuthenticationCommonAppleLoginSvg}
                alt="Apple login"
                width={24}
                height={24}
              />
            </DefaultButton>
          </form>
        </div>
        <div className="tw-mt-7 tw-flex tw-items-center tw-justify-center">
          <p className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-font-plusJakartaSans tw-text-lg tw-font-medium tw-leading-[22.68px] tw-text-[#6C6C6C] max-nine:tw-text-sm">
            {translate(AuthenticationEnum.already_have_an_account)}{' '}
            <DefaultLink
              href="/auth/sign-in"
              className="tw-text-main tw-underline tw-underline-offset-4"
            >
              {translate(AuthenticationEnum.sign_in)}
            </DefaultLink>
          </p>
        </div>
      </AuthCommonLayout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = getSignUpServerSideProps;

SignUp.getLayout = function getLayout(page: ReactElement) {
  return <>{page}</>;
};
